import React, { useEffect, useState, useCallback } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    Table,
    Button,
    Modal,
    Form,
    Spinner,
    Alert,
    Badge,
    InputGroup,
} from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import axios from "axios";

const DailyExpensesPage = () => {
    // Data States
    const [expenses, setExpenses] = useState([]);
    const [expenseTypes, setExpenseTypes] = useState([]);
    const [banks, setBanks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [errors, setErrors] = useState({});

    // Filter States
    const [search, setSearch] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [selectedExpenseTypeId, setSelectedExpenseTypeId] = useState("");

    // Modal & Form States
    const [showModal, setShowModal] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [viewItem, setViewItem] = useState(null);

    const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
    const [note, setNote] = useState("");
    const [bankId, setBankId] = useState("");
    const [paymentDate, setPaymentDate] = useState("");
    const [paymentNotes, setPaymentNotes] = useState("");
    const [details, setDetails] = useState([
        { expense_type_id: "", amount: "" },
    ]);

    const getAuthHeaders = () => ({
        Authorization: localStorage.getItem("authToken")
            ? `Bearer ${localStorage.getItem("authToken")}`
            : "",
    });

    // Fetch Initial Options (Expense Types & Banks)
    const fetchDropdownOptions = async () => {
        try {
            const [typesRes, banksRes] = await Promise.all([
                axios.get(
                    `${process.env.REACT_APP_API_URL}/web-manager/expense-types?type=Daily Expense`,
                    { headers: getAuthHeaders() }
                ),
                axios.get(`${process.env.REACT_APP_API_URL}/web-manager/banks`, {
                    headers: getAuthHeaders(),
                }).catch(() => ({ data: [] })),
            ]);

            setExpenseTypes(typesRes.data?.data || typesRes.data || []);
            setBanks(banksRes.data?.data || banksRes.data || []);
        } catch (err) {
            console.error("Failed to load dropdown dependencies:", err);
        }
    };

    // Fetch Daily Expenses List
    const fetchExpenses = useCallback(async () => {
        try {
            setLoading(true);
            setError("");

            const params = {};
            if (search) params.search = search;
            if (fromDate) params.from_date = fromDate;
            if (toDate) params.to_date = toDate;
            if (selectedExpenseTypeId) params.expense_type_id = selectedExpenseTypeId;

            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/web-manager/daily-expenses`,
                {
                    headers: getAuthHeaders(),
                    params,
                }
            );

            setExpenses(response.data?.data || response.data || []);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to load daily expenses.");
        } finally {
            setLoading(false);
        }
    }, [search, fromDate, toDate, selectedExpenseTypeId]);

    useEffect(() => {
        fetchDropdownOptions();
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchExpenses();
        }, 300);

        return () => clearTimeout(timer);
    }, [fetchExpenses]);

    // Form Details Row Actions
    const handleAddDetailRow = () => {
        setDetails([...details, { expense_type_id: "", amount: "" }]);
    };

    const handleRemoveDetailRow = (index) => {
        if (details.length === 1) return;
        const updated = details.filter((_, i) => i !== index);
        setDetails(updated);
    };

    const handleDetailChange = (index, field, value) => {
        const updated = [...details];
        updated[index][field] = value;
        setDetails(updated);
        setErrors((prev) => ({ ...prev, [`details.${index}.${field}`]: "" }));
    };

    // Modal Actions
    const handleOpenModal = (item = null) => {
        setErrors({});
        if (item) {
            setEditItem(item);
            setDate(item.date || new Date().toISOString().split("T")[0]);
            setNote(item.note || "");
            setBankId(item.payment?.bank_id || "");
            setPaymentDate(item.payment?.payment_date || item.date || "");
            setPaymentNotes(item.payment?.notes || "");
            setDetails(
                item.details?.length > 0
                    ? item.details.map((d) => ({
                        expense_type_id: d.expense_type_id || "",
                        amount: d.amount || "",
                    }))
                    : [{ expense_type_id: "", amount: "" }]
            );
        } else {
            setEditItem(null);
            setDate(new Date().toISOString().split("T")[0]);
            setNote("");
            setBankId("");
            setPaymentDate("");
            setPaymentNotes("");
            setDetails([{ expense_type_id: "", amount: "" }]);
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditItem(null);
        setErrors({});
    };

    // Calculate Form Total
    const calculatedTotal = details.reduce(
        (sum, item) => sum + (parseFloat(item.amount) || 0),
        0
    );

    // Create / Update
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setErrors({});
        setMessage("");
        setError("");

        const payload = {
            date,
            note,
            details: details.map((d) => ({
                expense_type_id: d.expense_type_id,
                amount: parseFloat(d.amount) || 0,
            })),
            bank_id: bankId || null,
            payment_date: paymentDate || null,
            payment_notes: paymentNotes || null,
        };

        try {
            if (editItem) {
                await axios.put(
                    `${process.env.REACT_APP_API_URL}/web-manager/daily-expenses/${editItem.id}`,
                    payload,
                    { headers: getAuthHeaders() }
                );
                setMessage("Daily expense updated successfully.");
            } else {
                await axios.post(
                    `${process.env.REACT_APP_API_URL}/web-manager/daily-expenses`,
                    payload,
                    { headers: getAuthHeaders() }
                );
                setMessage("Daily expense recorded successfully.");
            }

            handleCloseModal();
            await fetchExpenses();
        } catch (err) {
            if (err.response?.status === 422) {
                setErrors(err.response.data.errors || {});
            } else {
                setError(err.response?.data?.message || "Something went wrong.");
            }
        } finally {
            setSaving(false);
        }
    };

    // Delete
    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this expense record?")) return;

        try {
            setError("");
            setMessage("");
            await axios.delete(
                `${process.env.REACT_APP_API_URL}/web-manager/daily-expenses/${id}`,
                { headers: getAuthHeaders() }
            );
            setMessage("Daily expense deleted successfully.");
            await fetchExpenses();
        } catch (err) {
            setError(err.response?.data?.message || "Failed to delete expense entry.");
        }
    };

    return (
        <PageLayout>
            <Container fluid className="py-4">
                {/* Header Section */}
                <Row className="align-items-center mb-4">
                    <Col>
                        <h2 className="fw-bold mb-1">Daily Expenses</h2>
                        <p className="text-muted mb-0">
                            Track and log day-to-day operational expenditures.
                        </p>
                    </Col>
                    <Col xs="auto">
                        <Button
                            variant="primary"
                            className="px-4 fw-semibold"
                            onClick={() => handleOpenModal()}
                        >
                            + Record Daily Expense
                        </Button>
                    </Col>
                </Row>

                {/* System Messages */}
                {message && (
                    <Alert variant="success" dismissible onClose={() => setMessage("")}>
                        {message}
                    </Alert>
                )}

                {error && (
                    <Alert variant="danger" dismissible onClose={() => setError("")}>
                        {error}
                    </Alert>
                )}

                {/* Filters Section */}
                <Card className="border-0 shadow-sm mb-4">
                    <Card.Body className="p-3">
                        <Row className="g-3">
                            <Col md={3}>
                                <InputGroup>
                                    <Form.Control
                                        type="text"
                                        placeholder="Search note or expense name..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        className="bg-light border-0 py-2"
                                    />
                                </InputGroup>
                            </Col>
                            <Col md={3}>
                                <Form.Select
                                    value={selectedExpenseTypeId}
                                    onChange={(e) => setSelectedExpenseTypeId(e.target.value)}
                                    className="bg-light border-0 py-2"
                                >
                                    <option value="">All Expense Types</option>
                                    {expenseTypes.map((t) => (
                                        <option key={t.id} value={t.id}>
                                            {t.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Col>
                            <Col md={3}>
                                <Form.Control
                                    type="date"
                                    value={fromDate}
                                    onChange={(e) => setFromDate(e.target.value)}
                                    className="bg-light border-0 py-2"
                                    placeholder="From Date"
                                />
                            </Col>
                            <Col md={3}>
                                <Form.Control
                                    type="date"
                                    value={toDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                    className="bg-light border-0 py-2"
                                    placeholder="To Date"
                                />
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* Table Section */}
                <Card className="border-0 shadow-sm">
                    <Card.Body className="p-0">
                        {loading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <div className="mt-2 text-muted">Loading daily expenses...</div>
                            </div>
                        ) : expenses.length === 0 ? (
                            <div className="text-center py-5 text-muted">
                                No expense records found. Click "+ Record Daily Expense" to add one.
                            </div>
                        ) : (
                            <Table responsive hover className="align-middle mb-0">
                                <thead className="bg-light">
                                    <tr>
                                        <th className="ps-4" style={{ width: "70px" }}>#</th>
                                        <th>Date</th>
                                        <th>Note / Particulars</th>
                                        <th>Entered By</th>
                                        <th>Breakdown</th>
                                        <th>Payment Account</th>
                                        <th>Total Amount</th>
                                        <th className="text-end pe-4">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {expenses.map((item, index) => (
                                        <tr key={item.id || index}>
                                            <td className="ps-4 fw-bold text-muted">{index + 1}</td>
                                            <td className="fw-semibold">{item.date}</td>
                                            <td style={{ maxWidth: "250px" }} className="text-truncate">
                                                {item.note}
                                            </td>
                                            <td className="text-secondary small">
                                                {item.entered_by?.name || "N/A"}
                                            </td>
                                            <td>
                                                <Button
                                                    variant="link"
                                                    size="sm"
                                                    className="p-0 text-decoration-none fw-semibold"
                                                    onClick={() => {
                                                        setViewItem(item);
                                                        setShowDetailModal(true);
                                                    }}
                                                >
                                                    {item.details?.length || 0} item(s)
                                                </Button>
                                            </td>
                                            <td>
                                                {item.payment?.bank_name ? (
                                                    <Badge bg="light" className="text-dark border">
                                                        {item.payment.bank_name}
                                                    </Badge>
                                                ) : (
                                                    <Badge bg="secondary" className="bg-opacity-10 text-secondary">
                                                        Cash / Default
                                                    </Badge>
                                                )}
                                            </td>
                                            <td className="fw-bold text-dark">
                                                ${item.total_amount?.toFixed(2)}
                                            </td>
                                            <td className="text-end pe-4">
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    className="me-2"
                                                    onClick={() => handleOpenModal(item)}
                                                >
                                                    Edit
                                                </Button>
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    onClick={() => handleDelete(item.id)}
                                                >
                                                    Delete
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </Card.Body>
                </Card>

                {/* CREATE / EDIT EXPENSE MODAL */}
                <Modal
                    show={showModal}
                    onHide={handleCloseModal}
                    centered
                    size="lg"
                    backdrop="static"
                    keyboard={false}
                >
                    <Modal.Header closeButton className="border-0 px-4 pt-4 pb-0">
                        <Modal.Title className="fw-bold fs-4 text-dark">
                            {editItem ? "Edit Daily Expense" : "Record Daily Expense"}
                        </Modal.Title>
                    </Modal.Header>

                    <Form onSubmit={handleSubmit}>
                        <Modal.Body className="px-4 py-3">
                            <Row className="g-3 mb-3">
                                <Col md={6}>
                                    <Form.Label className="fw-semibold small text-muted text-uppercase">
                                        Expense Date <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={date}
                                        onChange={(e) => setDate(e.target.value)}
                                        isInvalid={!!errors.date}
                                        className="bg-light border-0 py-2"
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {Array.isArray(errors.date) ? errors.date[0] : errors.date}
                                    </Form.Control.Feedback>
                                </Col>

                                <Col md={6}>
                                    <Form.Label className="fw-semibold small text-muted text-uppercase">
                                        Note / Description <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="General note for this entry"
                                        value={note}
                                        onChange={(e) => setNote(e.target.value)}
                                        isInvalid={!!errors.note}
                                        className="bg-light border-0 py-2"
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {Array.isArray(errors.note) ? errors.note[0] : errors.note}
                                    </Form.Control.Feedback>
                                </Col>
                            </Row>

                            {/* Details Array Section */}
                            <div className="mb-4">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <Form.Label className="fw-semibold small text-muted text-uppercase m-0">
                                        Expense Breakdown Items <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={handleAddDetailRow}
                                    >
                                        + Add Item
                                    </Button>
                                </div>

                                {details.map((detail, idx) => (
                                    <Row key={idx} className="g-2 align-items-center mb-2">
                                        <Col md={7}>
                                            <Form.Select
                                                value={detail.expense_type_id}
                                                onChange={(e) =>
                                                    handleDetailChange(
                                                        idx,
                                                        "expense_type_id",
                                                        e.target.value
                                                    )
                                                }
                                                isInvalid={!!errors[`details.${idx}.expense_type_id`]}
                                                className="bg-light border-0 py-2"
                                            >
                                                <option value="">Select Expense Type</option>
                                                {expenseTypes.map((t) => (
                                                    <option key={t.id} value={t.id}>
                                                        {t.name}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                placeholder="Amount"
                                                value={detail.amount}
                                                onChange={(e) =>
                                                    handleDetailChange(idx, "amount", e.target.value)
                                                }
                                                isInvalid={!!errors[`details.${idx}.amount`]}
                                                className="bg-light border-0 py-2"
                                            />
                                        </Col>
                                        <Col md={1} className="text-end">
                                            {details.length > 1 && (
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() => handleRemoveDetailRow(idx)}
                                                >
                                                    ✕
                                                </Button>
                                            )}
                                        </Col>
                                    </Row>
                                ))}
                                <div className="text-end fw-bold text-dark mt-2">
                                    Total: ${calculatedTotal.toFixed(2)}
                                </div>
                            </div>

                            <hr className="my-3" />

                            {/* Payment Method Optional Breakdown */}
                            <h6 className="fw-bold text-muted small text-uppercase mb-3">
                                Payment Details (Optional)
                            </h6>

                            <Row className="g-3">
                                <Col md={6}>
                                    <Form.Label className="small text-muted">Bank / Account</Form.Label>
                                    <Form.Select
                                        value={bankId}
                                        onChange={(e) => setBankId(e.target.value)}
                                        className="bg-light border-0 py-2"
                                    >
                                        <option value="">Cash / None</option>
                                        {banks.map((b) => (
                                            <option key={b.id} value={b.id}>
                                                {b.name || b.bank_name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Col>
                                <Col md={6}>
                                    <Form.Label className="small text-muted">Payment Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={paymentDate}
                                        onChange={(e) => setPaymentDate(e.target.value)}
                                        className="bg-light border-0 py-2"
                                    />
                                </Col>
                                <Col md={12}>
                                    <Form.Label className="small text-muted">Payment Notes</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Check # or transaction ref"
                                        value={paymentNotes}
                                        onChange={(e) => setPaymentNotes(e.target.value)}
                                        className="bg-light border-0 py-2"
                                    />
                                </Col>
                            </Row>
                        </Modal.Body>

                        <Modal.Footer className="border-0 px-4 pb-4 pt-2 d-flex justify-content-end gap-2">
                            <Button
                                variant="secondary"
                                className="px-4 py-2 fw-semibold"
                                onClick={handleCloseModal}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={saving}
                                className="px-4 py-2 fw-semibold text-uppercase"
                            >
                                {saving ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Saving...
                                    </>
                                ) : editItem ? (
                                    "Update Expense"
                                ) : (
                                    "Save Expense"
                                )}
                            </Button>
                        </Modal.Footer>
                    </Form>
                </Modal>

                {/* BREAKDOWN ITEMS VIEW MODAL */}
                <Modal
                    show={showDetailModal}
                    onHide={() => setShowDetailModal(false)}
                    centered
                >
                    <Modal.Header closeButton className="border-0 px-4 pt-4 pb-0">
                        <Modal.Title className="fw-bold fs-5">
                            Expense Items Details
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="p-4">
                        {viewItem && (
                            <>
                                <Table hover responsive className="align-middle">
                                    <thead className="bg-light">
                                        <tr>
                                            <th>Type</th>
                                            <th className="text-end">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {viewItem.details?.map((d, i) => (
                                            <tr key={i}>
                                                <td>{d.expense_type || "N/A"}</td>
                                                <td className="text-end fw-semibold">
                                                    ${parseFloat(d.amount).toFixed(2)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                                <div className="d-flex justify-content-between fw-bold fs-6 border-top pt-2">
                                    <span>Total:</span>
                                    <span>${viewItem.total_amount?.toFixed(2)}</span>
                                </div>
                            </>
                        )}
                    </Modal.Body>
                </Modal>
            </Container>
        </PageLayout>
    );
};

export default DailyExpensesPage;